"use client"

import { useCallback, useEffect, useMemo, useState, useRef } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Table, TableBody, TableHeader } from "@/components/ui/table"
import {
  CalendarIcon,
  ChevronRightIcon,
  ClockIcon,
  DollarSignIcon,
  FileTextIcon,
  CheckIcon,
  MinusCircleIcon
} from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { useCommissionDashboard, isAvailableForPayout, parseDateOnlyLocal } from "@/lib/commission-dashboard-service"
import { formatCurrency, formatDate } from "@/lib/formatting"
import { CommissionTransaction, CommissionUser, CommissionPeriod } from "@/types/commission-dashboard"
import { TransactionType, CodeValueGroups, PayoutType, CommissionAdjustmentHeaderType } from "@/types/constants"
import { format } from "date-fns"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { useCommission } from "@/contexts/commission-context"
import { useAuth } from "@/contexts/auth-context"
import { useCode } from "@/contexts/code-context"
import { useLanguage } from "@/contexts/language-context"
import { useNavigation } from '@/contexts/navigation-context'
import { logger } from "@/lib/logger"
import { DeferCommissionModal } from "@/components/dashboard/DeferCommissionModal"
import { Filter } from "@/components/ui/filter-input"
import FilterRow, { 
  CustomFilterHandler,
  FilterState, 
  applyFilters as applyFiltersUtil
} from "@/components/ui/filter-row"
import TableHeaderRow, { applySorting } from "@/components/ui/table-header-row"
import type { SortConfig, SortingConfig } from "@/components/ui/table-header-row"
import Pagination from "@/components/ui/pagination"
import { TableColumnConfig } from "@/types/table"
import TableRowContent from "@/components/ui/table-row-content"
import { calculatePayoutSummary } from "@/lib/payout-calculations"
import { useToast } from "@/hooks/use-toast"

interface CommissionDashboardContentProps {
  lastPayoutText: string
  processedDateText: string
}

function splitByPayoutAvailability(
  transactions: CommissionTransaction[],
  payoutType: PayoutType,
  plannedPaymentDate: string
) {
  return {
    current: transactions.filter(t => isAvailableForPayout(t, payoutType, plannedPaymentDate)),
    future: transactions.filter(t => !isAvailableForPayout(t, payoutType, plannedPaymentDate)),
  }
}

const showCheckbox = true; // Since we always show checkboxes in this component

export function CommissionDashboardContent({
  lastPayoutText,
  processedDateText
}: Readonly<CommissionDashboardContentProps>) {
  // 1. Context/Router Hooks
  const { token, isAuthenticated } = useAuth()
  const { navigateTo } = useNavigation()
  const { setSelectedTransactionsState } = useCommission()
  const { scheduleTransactions, deferTransactions, getUnpaidTransactions, generateRecurringAdjustments, getCommissionUsers, getOpeningBalances, getCurrentPeriod, finalizePeriod } = useCommissionDashboard()
  const { getCodeText } = useCode()
  const { dictionary, language } = useLanguage()
  const { toast } = useToast()
  
  // 2. State Hooks
  const [selectedCurrentIds, setSelectedCurrentIds] = useState<Set<number>>(new Set())
  const [selectedFutureIds, setSelectedFutureIds] = useState<Set<number>>(new Set())
  const [activeTab, setActiveTab] = useState("current")
  const [isInitializing, setIsInitializing] = useState(true)
  const [isGeneratingDraws, setIsGeneratingDraws] = useState(false)
  const [isFinalizing, setIsFinalizing] = useState(false)
  const [actionDialog, setActionDialog] = useState<'generateDraws' | 'finalize' | null>(null)
  const [isDeferModalOpen, setIsDeferModalOpen] = useState(false)
  const [isDeferring, setIsDeferring] = useState(false)
  const [isProceeding, setIsProceeding] = useState(false)
  const proceedRequestIdRef = useRef(0)
  const isInitialized = useRef(false)
  const [allTransactions, setAllTransactions] = useState<CommissionTransaction[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [futurePage, setFuturePage] = useState(1)
  const ITEMS_PER_PAGE = 20
  const [sortConfig, setSortConfig] = useState<SortConfig>({ column: '', direction: null })
  const [filters, setFilters] = useState<FilterState>({})
  const [payoutType, setPayoutType] = useState<PayoutType>('scheduled')
  const [currentPeriod, setCurrentPeriod] = useState<CommissionPeriod | null>(null)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null)
  const [commissionUsers, setCommissionUsers] = useState<CommissionUser[]>([])
  const [hasProceeded, setHasProceeded] = useState(false)
  const [openingBalances, setOpeningBalances] = useState<Record<number, number>>({})
  const hasLoadedUsers = useRef(false)

  // 3. Initialize - load current period and mark as initialized
  useEffect(() => {
    if (!isAuthenticated || !token || isInitialized.current) {
      setIsInitializing(false)
      return
    }

    isInitialized.current = true

    const loadCurrentPeriod = async () => {
      try {
        const period = await getCurrentPeriod()
        setCurrentPeriod(period)
      } catch (error) {
        logger.error(error)
      } finally {
        setIsInitializing(false)
      }
    }

    loadCurrentPeriod()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token])

  // Load commission users on mount (only needed for adhoc dropdown)
  useEffect(() => {
    if (!isAuthenticated || !token || hasLoadedUsers.current) return
    
    hasLoadedUsers.current = true
    
    const loadUsers = async () => {
      try {
        const users = await getCommissionUsers()
        setCommissionUsers(users)
      } catch (error) {
        logger.error(error)
        hasLoadedUsers.current = false // Reset on error so it can retry
        
        // Show error toast if available
        if (toast && dictionary?.dashboard?.messages?.usersError) {
          const errorMessage = error instanceof Error ? error.message : (dictionary?.dashboard?.messages?.usersErrorDescription ?? "Failed to load commission users for adhoc dropdown.")
          
          toast({
            title: dictionary.dashboard.messages.usersError,
            description: errorMessage,
            variant: "destructive"
          })
        }
      }
    }
    
    loadUsers()
  // Only run on mount/auth change - getCommissionUsers, toast, and dictionary are stable enough
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token])

  // Reset all payout state when the user changes payout type, statement period, or employee selection.
  // Kept separate from the re-split effect so a data refresh (allTransactions change) doesn't
  // inadvertently hide the summary/grid after Proceed has been clicked.
  useEffect(() => {
    proceedRequestIdRef.current++
    setHasProceeded(false)
    setIsProceeding(false)
    setSelectedCurrentIds(new Set())
    setSelectedFutureIds(new Set())
    setOpeningBalances({})
  }, [payoutType, currentPeriod, selectedEmployeeId])

  // Re-split transactions whenever the source data or filtering criteria change.
  // Derived rather than stored so the split is always correct in the same commit
  // that allTransactions changes — no lagging-by-one-effect-pass intermediate state.
  const { current: currentTabTransactions, future: futureTabTransactions } = useMemo(
    () => splitByPayoutAvailability(allTransactions, payoutType, currentPeriod?.plannedPaymentDate ?? ''),
    [allTransactions, payoutType, currentPeriod]
  )

  // Determine if user can proceed based on payout type
  const canProceed = useMemo(() => {
    if (payoutType === 'scheduled') {
      return currentPeriod !== null
    } else {
      return selectedEmployeeId !== null
    }
  }, [payoutType, selectedEmployeeId, currentPeriod])

  // Filter transactions based on payout type and selections
  const filteredTransactionsByPayoutType = useMemo(() => {
    if (!hasProceeded) {
      return { current: [], future: [] }
    }

    if (payoutType === 'adhoc' && selectedEmployeeId) {
      // Filter by selected employee
      const selectedUser = commissionUsers.find(u => u.profileId === selectedEmployeeId)
      if (!selectedUser) {
        return { current: [], future: [] }
      }

      const current = currentTabTransactions.filter(t => t.commissionUserContactId === selectedUser.contactId)
      const future = futureTabTransactions.filter(t => t.commissionUserContactId === selectedUser.contactId)
      return { current, future }
    }

    // For scheduled payout, show all transactions
    return {
      current: currentTabTransactions,
      future: futureTabTransactions
    }
  }, [hasProceeded, payoutType, selectedEmployeeId, commissionUsers, currentTabTransactions, futureTabTransactions])

  // Check if the current period is overdue (PlannedPaymentDate has passed)
  const isPeriodOverdue = useMemo(() => {
    if (!currentPeriod) return false
    const plannedDate = parseDateOnlyLocal(currentPeriod.plannedPaymentDate)
    if (!plannedDate) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return plannedDate < today
  }, [currentPeriod])

  // Define columns configuration
  const columnsConfig = useMemo<TableColumnConfig<CommissionTransaction>[]>(() => [
    {
      key: 'commissionUserFullName',
      label: dictionary?.dashboard?.transactions?.columns?.employeeName ?? 'Employee Name',
      width: 'w-[200px]',
      type: 'string',
      sortable: true,
      filterable: true,
    },
    {
      key: 'commissionRoleCode',
      label: dictionary?.dashboard?.transactions?.columns?.commissionRole ?? "Commission Role",
      width: 'w-[220px]',
      type: 'string',
      sortable: true,
      filterable: true,
      getFilterOptions: (data: CommissionTransaction[]) => Array.from(new Set(data.map(item => item.commissionRoleCode)))
        .filter((code): code is string => code !== null && code !== undefined && code !== '')
        .map(code => ({ 
          value: code, 
          label: getCodeText(CodeValueGroups.CommissionRole, code) 
        })),
      displayValue: (item: CommissionTransaction) => getCodeText(CodeValueGroups.CommissionRole, item.commissionRoleCode || '')
    },
    {
      key: 'commissionRateDescription',
      label: dictionary?.dashboard?.transactions?.columns?.commissionRateDescription ?? 'Commission Description',
      width: 'w-[240px]',
      type: 'string',
      sortable: true,
      filterable: true,
    },    
    {
      key: 'commissionAdjustmentDescription',
      label: dictionary?.dashboard?.transactions?.columns?.commissionAdjustmentDescription ?? 'Commission Adjustment Description',
      width: 'w-[300px]',
      type: 'string',
      sortable: true,
      filterable: true,
    },
    {
      key: 'transactionTypeCode',
      label: dictionary?.dashboard?.transactions?.columns?.transactionType ?? 'Transaction Type',
      width: 'w-[180px]',
      type: 'string',
      sortable: true,
      customSort: (a: CommissionTransaction, b: CommissionTransaction) => {
        // Sort order: Draws, Adjustments, Permanent, Standard
        const getTypePriority = (item: CommissionTransaction) => {
          if (item.commissionAdjustmentHeaderTypeCode === CommissionAdjustmentHeaderType.Draws) return 0
          if (item.commissionAdjustmentHeaderTypeCode !== null) return 1
          if (item.transactionTypeCode === TransactionType.PermanentPlacement) return 2
          return 3
        }
        return getTypePriority(a) - getTypePriority(b)
      },
      filterable: true,
      getFilterOptions: () => [
        { value: 'Draw', label: dictionary?.dashboard?.transactionTypes?.draw ?? 'Draw' },
        { value: 'Adjustment', label: dictionary?.dashboard?.transactionTypes?.adjustment ?? 'Adjustment' },
        { value: TransactionType.PermanentPlacement, label: dictionary?.dashboard?.transactionTypes?.permanent ?? 'Permanent' },
        { value: 'Standard', label: dictionary?.dashboard?.transactionTypes?.standard ?? 'Standard' }
      ],
      customFilter: (item: CommissionTransaction, filter: Filter) => {
        const filterValues = typeof filter.value === 'string' ? filter.value.split(',') : [filter.value]
        return filterValues.some(val => {
          if (val === 'Draw') {
            return item.commissionAdjustmentHeaderTypeCode === CommissionAdjustmentHeaderType.Draws
          }
          if (val === 'Adjustment') {
            return item.commissionAdjustmentHeaderTypeCode !== null && item.commissionAdjustmentHeaderTypeCode !== CommissionAdjustmentHeaderType.Draws
          }
          if (val === 'Standard') {
            return item.commissionAdjustmentHeaderTypeCode === null && item.transactionTypeCode !== TransactionType.PermanentPlacement
          }
          if (val === TransactionType.PermanentPlacement) {
            return item.transactionTypeCode === TransactionType.PermanentPlacement && item.commissionAdjustmentHeaderTypeCode === null
          }
          return false
        })
      },
      customRender: (item) => {
        // Show Draw badge if it's a draw adjustment
        if (item.commissionAdjustmentHeaderTypeCode === CommissionAdjustmentHeaderType.Draws) {
          return (
            <Badge 
              variant="outline" 
              className="bg-[#FFF4E6] text-orange-700 border border-orange-200 hover:bg-[#FFF4E6] hover:text-orange-700"
            >
              {dictionary?.dashboard?.transactionTypes?.draw ?? "Draw"}
            </Badge>
          )
        }
        
        // Show Adjustment badge if it's any other adjustment
        if (item.commissionAdjustmentHeaderTypeCode !== null) {
          return (
            <Badge 
              variant="outline" 
              className="bg-[#F3E8FF] text-purple-700 border border-purple-200 hover:bg-[#F3E8FF] hover:text-purple-700"
            >
              {dictionary?.dashboard?.transactionTypes?.adjustment ?? "Adjustment"}
            </Badge>
          )
        }
        
        // Show Permanent or Standard for regular commissions
        return (
          <Badge 
            variant="outline" 
            className={item.transactionTypeCode === TransactionType.PermanentPlacement 
              ? "bg-[#E9EEF5] text-blue-700 border border-blue-200 hover:bg-[#E9EEF5] hover:text-blue-700" 
              : "bg-[#E9F2EC] text-green-700 border border-green-200 hover:bg-[#E9F2EC] hover:text-green-700"
            }
          >
            {item.transactionTypeCode === TransactionType.PermanentPlacement 
              ? (dictionary?.dashboard?.transactionTypes?.permanent ?? "Permanent") 
              : (dictionary?.dashboard?.transactionTypes?.standard ?? "Standard")
            }
          </Badge>
        )
      }
    },
    {
      key: 'transactionDate',
      label: dictionary?.dashboard?.transactions?.columns?.transactionDate ?? 'Transaction Date',
      width: 'w-[180px]',
      type: 'date',
      sortable: true,
      filterable: true,
      formatOptions: {
        language: language
      }
    },
    {
      key: 'clientOrganizationLegalName',
      label: dictionary?.dashboard?.transactions?.columns?.client ?? 'Client',
      width: 'w-[220px]',
      type: 'string',
      sortable: true,
      filterable: true,
    },
    {
      key: 'workerName',
      label: dictionary?.dashboard?.transactions?.columns?.workerName ?? 'Worker Name',
      width: 'w-[200px]',
      type: 'string',
      sortable: true,
      filterable: true,
    },
    {
      key: 'transactionNumber',
      label: dictionary?.dashboard?.transactions?.columns?.transactionNumber ?? 'Transaction Number',
      width: 'w-[200px]',
      type: 'string',
      sortable: true,
      filterable: true,
    },
    {
      key: 'startDate',
      label: dictionary?.dashboard?.transactions?.columns?.startDate ?? 'Start Date',
      width: 'w-[210px]',
      type: 'date',
      sortable: true,
      filterable: true,
      formatOptions: {
        language: language
      }
    },
    {
      key: 'endDate',
      label: dictionary?.dashboard?.transactions?.columns?.endDate ?? 'End Date',
      width: 'w-[210px]',
      type: 'date',
      sortable: true,
      filterable: true,
      formatOptions: {
        language: language
      }
    },
    {
      key: 'grossProfit',
      label: dictionary?.dashboard?.transactions?.columns?.grossMargin ?? 'Gross Margin',
      width: 'w-[210px]',
      type: 'currency',
      sortable: true,
      filterable: true,
    },
    {
      key: 'revenueAmount',
      label: dictionary?.dashboard?.transactions?.columns?.revenue ?? 'Revenue',
      width: 'w-[210px]',
      type: 'currency',
      sortable: true,
      filterable: true,
    },
    {
      key: 'marginSplitPercentage',
      label: dictionary?.dashboard?.transactions?.columns?.commissionSplit ?? 'Commission Split %',
      width: 'w-[210px]',
      type: 'percentage',
      sortable: true,
      filterable: true,
    },
    {
      key: 'commissionPercentage',
      label: dictionary?.dashboard?.transactions?.columns?.commission ?? 'Commission %',
      width: 'w-[210px]',
      type: 'percentage',
      sortable: true,
      filterable: true,
    },
    {
      key: 'commissionCurrencyCode',
      label: dictionary?.dashboard?.transactions?.columns?.currency ?? 'Currency',
      width: 'w-[160px]',
      type: 'string',
      sortable: true,
      filterable: true,
      getFilterOptions: (data: CommissionTransaction[]) => Array.from(new Set(data.map(item => item.commissionCurrencyCode)))
        .filter((code): code is string => code !== null && code !== undefined)
        .map(code => ({ value: code, label: code }))
    },
    {
      key: 'commissionAmount',
      label: dictionary?.dashboard?.transactions?.columns?.commissionAmount ?? 'Commission Amount',
      width: 'w-[200px]',
      type: 'currency',
      sortable: true,
      filterable: true,
    },
    {
      key: 'invoiceARStatusCode',
      label: dictionary?.dashboard?.transactions?.columns?.invoiceARStatus ?? 'Invoice AR Status',
      width: 'w-[200px]',
      type: 'string',
      sortable: true,
      filterable: true,
      getFilterOptions: (data: CommissionTransaction[]) => Array.from(new Set(data.map(item => item.invoiceARStatusCode)))
        .filter((code): code is string => code !== null && code !== undefined)
        .map(code => ({ 
          value: code, 
          label: getCodeText(CodeValueGroups.ARStatus, code) 
        })),
      displayValue: (item: CommissionTransaction) => getCodeText(CodeValueGroups.ARStatus, item.invoiceARStatusCode || '')
    },
    {
      key: 'permanentPlacementGuaranteeDate',
      label: dictionary?.dashboard?.transactions?.columns?.permGuaranteeDate ?? 'Perm Guarantee Date',
      width: 'w-[210px]',
      type: 'date',
      sortable: true,
      filterable: true,
      formatOptions: {
        language: language
      }
    },
    {
      key: 'scheduledPaymentDate',
      label: dictionary?.dashboard?.transactions?.columns?.scheduledPaymentDate ?? 'Scheduled Payment Date',
      width: 'w-[230px]',
      type: 'date',
      sortable: true,
      filterable: true,
      formatOptions: {
        language: language
      }
    },
    {
      key: 'invoiceNumber',
      label: dictionary?.dashboard?.transactions?.columns?.invoiceNumber ?? 'Invoice Number',
      width: 'w-[180px]',
      type: 'string',
      sortable: true,
      filterable: true,
    },
    {
      key: 'isInvoiceReleased',
      label: dictionary?.dashboard?.transactions?.columns?.invoiceReleased ?? 'Invoice Released',
      width: 'w-[190px]',
      type: 'string',
      sortable: true,
      filterable: true,
      getFilterOptions: () => [
        { value: 'true', label: dictionary?.app?.yes ?? 'Yes' },
        { value: 'false', label: dictionary?.app?.no ?? 'No' }
      ],
      customFilter: (item: CommissionTransaction, filter: Filter) => {
        const filterValues = typeof filter.value === 'string' ? filter.value.split(',') : [filter.value]
        const itemValue = item.isInvoiceReleased === null ? 'null' : String(item.isInvoiceReleased)
        return filterValues.some(val => itemValue === val)
      },
      displayValue: (item: CommissionTransaction) => {
        if (item.isInvoiceReleased === null) return ''
        return item.isInvoiceReleased ? (dictionary?.app?.yes ?? 'Yes') : (dictionary?.app?.no ?? 'No')
      },
    },
    {
      key: 'invoiceDate',
      label: dictionary?.dashboard?.transactions?.columns?.invoiceDate ?? 'Invoice Date',
      width: 'w-[180px]',
      type: 'date',
      sortable: true,
      filterable: true,
      formatOptions: {
        language: language
      }
    },
    {
      key: 'clientPaymentDate',
      label: dictionary?.dashboard?.transactions?.columns?.clientPaymentDate ?? 'Client Payment Date',
      width: 'w-[210px]',
      type: 'date',
      sortable: true,
      filterable: true,
      formatOptions: {
        language: language
      }
    },
    {
      key: 'billingTransactionStatusCode',
      label: dictionary?.dashboard?.transactions?.columns?.billingTransactionStatus ?? 'Billing Transaction Status',
      width: 'w-[240px]',
      type: 'string',
      sortable: true,
      filterable: true,
      getFilterOptions: (data: CommissionTransaction[]) => Array.from(new Set(data.map(item => item.billingTransactionStatusCode)))
        .filter((code): code is string => code !== null && code !== undefined)
        .map(code => ({ value: code, label: getCodeText(CodeValueGroups.BillingTransactionStatus, code) })),
      displayValue: (item: CommissionTransaction) => getCodeText(CodeValueGroups.BillingTransactionStatus, item.billingTransactionStatusCode ?? '')
    },
    {
      key: 'commissionPlanTierNumber',
      label: dictionary?.dashboard?.transactions?.columns?.tierLevel ?? 'Tier Level',
      width: 'w-[220px]',
      type: 'number',
      sortable: true,
      filterable: true,
      customSort: (a: CommissionTransaction, b: CommissionTransaction) => {
        const aVal = a.commissionPlanTierNumber !== null ? Number(a.commissionPlanTierNumber) : -Infinity
        const bVal = b.commissionPlanTierNumber !== null ? Number(b.commissionPlanTierNumber) : -Infinity
        return aVal - bVal
      },
    },
    {
      key: 'commissionAccrualDate',
      label: dictionary?.dashboard?.transactions?.columns?.commissionAccrualDate ?? 'Commission Accrual Date',
      width: 'w-[230px]',
      type: 'date',
      sortable: true,
      filterable: true,
      formatOptions: {
        language: language
      }
    },
    {
      key: 'internalOrganizationName',
      label: dictionary?.dashboard?.transactions?.columns?.internalCompany ?? 'Internal Company',
      width: 'w-[220px]',
      type: 'string',
      sortable: true,
      filterable: true,
    },
  ], [dictionary, language, getCodeText])

  // Create sorting config from columns config
  const sortingConfig = useMemo<SortingConfig>(() => {
    const config: SortingConfig = {}
    columnsConfig.forEach(col => {
      if (col.sortable) {
        config[String(col.key)] = {
          type: col.type,
          customSort: col.customSort
        }
      }
    })
    return config
  }, [columnsConfig])

  // Filter and sort transactions
  const processedCurrentTransactions = useMemo(() => {
    // Use filtered transactions if user has proceeded, otherwise use all
    const baseTransactions = hasProceeded ? filteredTransactionsByPayoutType.current : currentTabTransactions
    let result = [...baseTransactions]

    // Apply filters
    if (Object.keys(filters).length > 0) {
      result = applyFiltersUtil(result, filters, { 
        columnTypes: Object.fromEntries(columnsConfig.map(col => [col.key, col.type])),
        customHandlers: columnsConfig.reduce((acc, col) => {
          if (col.customFilter) {
            acc[String(col.key)] = col.customFilter
          }
          return acc
        }, {} as Record<string, CustomFilterHandler>)
      })
    }

    // Apply sorting
    result = applySorting(result, sortConfig, sortingConfig)

    return result
  }, [hasProceeded, filteredTransactionsByPayoutType, currentTabTransactions, filters, sortConfig, sortingConfig, columnsConfig])

  const processedFutureTransactions = useMemo(() => {
    // Use filtered transactions if user has proceeded, otherwise use all
    const baseTransactions = hasProceeded ? filteredTransactionsByPayoutType.future : futureTabTransactions
    let result = [...baseTransactions]

    // Apply filters
    if (Object.keys(filters).length > 0) {
      result = applyFiltersUtil(result, filters, { 
        columnTypes: Object.fromEntries(columnsConfig.map(col => [col.key, col.type])),
        customHandlers: columnsConfig.reduce((acc, col) => {
          if (col.customFilter) {
            acc[String(col.key)] = col.customFilter
          }
          return acc
        }, {} as Record<string, CustomFilterHandler>)
      })
    }

    // Apply sorting
    result = applySorting(result, sortConfig, sortingConfig)

    return result
  }, [hasProceeded, filteredTransactionsByPayoutType, futureTabTransactions, filters, sortConfig, sortingConfig, columnsConfig])

  const checkboxStates = useMemo(() => {
    const currentFilteredLength = processedCurrentTransactions.length
    const futureFilteredLength = processedFutureTransactions.length
    const currentBaseLength = hasProceeded ? filteredTransactionsByPayoutType.current.length : currentTabTransactions.length
    const futureBaseLength = hasProceeded ? filteredTransactionsByPayoutType.future.length : futureTabTransactions.length

    return {
      currentAllChecked: currentFilteredLength > 0 &&
        selectedCurrentIds.size === (Object.keys(filters).length > 0 ? currentFilteredLength : currentBaseLength),
      futureAllChecked: futureFilteredLength > 0 &&
        selectedFutureIds.size === (Object.keys(filters).length > 0 ? futureFilteredLength : futureBaseLength)
    }
  }, [
    currentTabTransactions.length,
    futureTabTransactions.length,
    processedCurrentTransactions.length,
    processedFutureTransactions.length,
    selectedCurrentIds.size,
    selectedFutureIds.size,
    filters,
    hasProceeded,
    filteredTransactionsByPayoutType
  ])

  // 5. Optimized Event Handlers
  const handleCurrentCheckboxChange = useCallback((id: number, checked: boolean) => {
    setSelectedCurrentIds(prev => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const handleCurrentSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const baseTransactions = hasProceeded ? filteredTransactionsByPayoutType.current : currentTabTransactions
      const transactionsToSelect = Object.keys(filters).length > 0
        ? processedCurrentTransactions.map(t => t.id)
        : baseTransactions.map(t => t.id)
      setSelectedCurrentIds(new Set(transactionsToSelect))
    } else {
      setSelectedCurrentIds(new Set())
    }
  }, [currentTabTransactions, processedCurrentTransactions, filters, hasProceeded, filteredTransactionsByPayoutType])

  const handleFutureCheckboxChange = useCallback((id: number, checked: boolean) => {
    setSelectedFutureIds(prev => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const handleFutureSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const baseTransactions = hasProceeded ? filteredTransactionsByPayoutType.future : futureTabTransactions
      const transactionsToSelect = Object.keys(filters).length > 0
        ? processedFutureTransactions.map(t => t.id)
        : baseTransactions.map(t => t.id)
      setSelectedFutureIds(new Set(transactionsToSelect))
    } else {
      setSelectedFutureIds(new Set())
    }
  }, [futureTabTransactions, processedFutureTransactions, filters, hasProceeded, filteredTransactionsByPayoutType])

  // Handle filter changes
  const handleFilterChange = useCallback((column: string, filter: Filter | undefined) => {
    setFilters(prevFilters => {
      if (!filter) {
        const newFilters = { ...prevFilters }
        delete newFilters[column]
        return newFilters
      }
      return {
        ...prevFilters,
        [column]: filter
      }
    })
    // Reset to first page when filter changes
    setCurrentPage(1)
    setFuturePage(1)
    // Clear selections when filters change
    setSelectedCurrentIds(new Set())
    setSelectedFutureIds(new Set())
  }, [])

  // Handle clearing filters
  const handleClearFilters = useCallback(() => {
    setFilters({})
    // Reset to first page when clearing filters
    setCurrentPage(1)
    setFuturePage(1)
    // Clear selections when clearing filters
    setSelectedCurrentIds(new Set())
    setSelectedFutureIds(new Set())
  }, [])

  // 6. Optimized Calculations
  const payoutSummary = useMemo(() => {
    // Get all selected transactions
    const selectedTransactions = currentTabTransactions.filter(t => selectedCurrentIds.has(t.id))
    
    if (selectedTransactions.length === 0) {
      return {
        openingBalance: 0,
        contractCommissions: 0,
        permPlacementCommissions: 0,
        adjustments: 0,
        draws: 0,
        closingBalance: 0,
        netChange: 0,
        totalPayable: 0,
        includesDrawsPayable: 0,
        hasSelectedTransactions: false
      }
    }

    const summary = calculatePayoutSummary(selectedTransactions, openingBalances)
    
    return {
      ...summary,
      hasSelectedTransactions: true
    }
  }, [currentTabTransactions, selectedCurrentIds, openingBalances])

  // Update pagination calculations
  const currentPageTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
    return processedCurrentTransactions.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  }, [currentPage, processedCurrentTransactions])

  const futurePageTransactions = useMemo(() => {
    const startIndex = (futurePage - 1) * ITEMS_PER_PAGE
    return processedFutureTransactions.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  }, [futurePage, processedFutureTransactions])

  // Helper function to handle column click
  const handleColumnClick = useCallback((column: string) => {
    setSortConfig(current => {
      if (current.column !== column) {
        return { column, direction: 'asc' }
      }
      if (current.direction === 'asc') {
        return { column, direction: 'desc' }
      }
      if (current.direction === 'desc') {
        return { column: '', direction: null }
      }
      return { column, direction: 'asc' }
    })
  }, [])

  // Replace PaginationDisplay and FuturePaginationDisplay with:
  const currentPagination = useMemo(() => (
    <Pagination
      currentPage={currentPage}
      totalItems={processedCurrentTransactions.length}
      itemsPerPage={ITEMS_PER_PAGE}
      onPageChange={setCurrentPage}
      dictionary={dictionary}
    />
  ), [currentPage, processedCurrentTransactions.length, ITEMS_PER_PAGE])

  const futurePagination = useMemo(() => (
    <Pagination
      currentPage={futurePage}
      totalItems={processedFutureTransactions.length}
      itemsPerPage={ITEMS_PER_PAGE}
      onPageChange={setFuturePage}
      dictionary={dictionary}
    />
  ), [futurePage, processedFutureTransactions.length, ITEMS_PER_PAGE])

  // 7. Action Handlers

  // Shared function to refresh dashboard data after transaction changes
  const refreshDashboardData = useCallback(async () => {
    const updatedTransactions = await getUnpaidTransactions()

    setAllTransactions(updatedTransactions)

    // Returned for callers that need the split synchronously (e.g. auto-selecting the
    // freshly fetched rows); the rendered tabs derive from allTransactions above.
    const { current, future } = splitByPayoutAvailability(
      updatedTransactions,
      payoutType,
      currentPeriod?.plannedPaymentDate ?? ''
    )

    return { current, future, updatedTransactions }
  }, [getUnpaidTransactions, payoutType, currentPeriod])

  const handleProceedClick = useCallback(async () => {
    const requestId = ++proceedRequestIdRef.current
    setIsProceeding(true)
    // Fetch fresh transactions on every Proceed click so the grid always reflects the latest data
    let freshCurrent: CommissionTransaction[] = []
    let freshFuture: CommissionTransaction[] = []

    try {
      ({ current: freshCurrent, future: freshFuture } = await refreshDashboardData())
    } catch (error) {
      logger.error(error)
      const errorMessage = error instanceof Error ? error.message : (dictionary?.dashboard?.messages?.refreshErrorDescription ?? "Failed to refresh transactions.")
      toast({
        title: dictionary?.dashboard?.messages?.refreshError ?? "Error",
        description: errorMessage,
        variant: "destructive"
      })
      if (requestId === proceedRequestIdRef.current) setIsProceeding(false)
      return
    }

    if (requestId !== proceedRequestIdRef.current) return

    setHasProceeded(true)

    // Determine which transactions to select based on payout type
    let currentToSelect: CommissionTransaction[] = []
    let futureToSelect: CommissionTransaction[] = []

    if (payoutType === 'adhoc' && selectedEmployeeId) {
      // Filter by selected employee
      const selectedUser = commissionUsers.find(u => u.profileId === selectedEmployeeId)
      if (selectedUser) {
        currentToSelect = freshCurrent.filter(t => t.commissionUserContactId === selectedUser.contactId)
        futureToSelect = freshFuture.filter(t => t.commissionUserContactId === selectedUser.contactId)
      }
    } else {
      // For scheduled payout, select all transactions
      currentToSelect = freshCurrent
      futureToSelect = freshFuture
    }

    // Auto-select all filtered transactions (both current and future tabs)
    setSelectedCurrentIds(new Set(currentToSelect.map(t => t.id)))
    setSelectedFutureIds(new Set(futureToSelect.map(t => t.id)))

    // Fetch opening balances for scheduled payouts with a valid statement period
    const selectedTransactions = [...currentToSelect, ...futureToSelect]
    const uniqueUserIds = Array.from(new Set(selectedTransactions.map(t => t.commissionUserProfileId)))

    if (uniqueUserIds.length > 0 && payoutType === 'scheduled' && currentPeriod) {
      try {
        const balances = await getOpeningBalances({
          userIds: uniqueUserIds,
          statementDate: currentPeriod.timesheetEndDate
        })
        if (requestId !== proceedRequestIdRef.current) return
        setOpeningBalances(balances)
      } catch (error) {
        logger.error(error)

        // Extract error message if available
        const errorMessage = error instanceof Error ? error.message : (dictionary?.dashboard?.messages?.balanceErrorDescription ?? "Failed to load opening balances.")

        toast({
          title: dictionary?.dashboard?.messages?.balanceError ?? "Error",
          description: errorMessage,
          variant: "destructive"
        })
        setOpeningBalances({})
      }
    } else {
      setOpeningBalances({})
    }
    if (requestId === proceedRequestIdRef.current) setIsProceeding(false)
  }, [refreshDashboardData, payoutType, selectedEmployeeId, commissionUsers, currentPeriod, getOpeningBalances, toast, dictionary])

  const handlePrepareClick = useCallback(() => {
    const selectedTransactions = currentTabTransactions.filter(t => selectedCurrentIds.has(t.id))

    const statementDate = payoutType === 'scheduled'
      ? currentPeriod?.timesheetEndDate ?? format(new Date(), 'yyyy-MM-dd')
      : format(new Date(), 'yyyy-MM-dd')

    setSelectedTransactionsState({
      transactions: selectedTransactions,
      standardTotal: payoutSummary.contractCommissions,
      permanentTotal: payoutSummary.permPlacementCommissions,
      selectedTotal: payoutSummary.contractCommissions + payoutSummary.permPlacementCommissions,
      isAdhoc: payoutType === 'adhoc',
      statementDate: statementDate
    })

    navigateTo('prepare')
  }, [currentTabTransactions, selectedCurrentIds, payoutSummary, setSelectedTransactionsState, navigateTo, payoutType, currentPeriod])

  const handleReleaseCommission = useCallback(async () => {
    if (!isAuthenticated || !token || selectedFutureIds.size === 0) return

    try {
      // Pre-compute arrays once to avoid multiple iterations
      const selectedIds = Array.from(selectedFutureIds)
      
      // Determine scheduled date:
      // - Adhoc: today's date
      // - Scheduled: the current period's planned payment date
      let scheduledDate: Date
      if (payoutType === 'adhoc') {
        scheduledDate = new Date()
      } else {
        scheduledDate = parseDateOnlyLocal(currentPeriod?.plannedPaymentDate) ?? new Date()
      }

      // Schedule all selected transactions with the computed date
      await scheduleTransactions(selectedIds, scheduledDate)

      // Show success toast
      toast({
        title: dictionary?.dashboard?.messages?.releaseSuccess ?? "Success",
        description: `${selectedIds.length} ${dictionary?.dashboard?.messages?.releaseSuccessDescription ?? "transaction(s) scheduled successfully."}`,
        variant: "success"
      })

      // Refresh the dashboard data
      await refreshDashboardData()
      
      // Update selections - move selected future transactions to current
      setSelectedCurrentIds(prev => new Set([...Array.from(prev), ...selectedIds]))
      setSelectedFutureIds(new Set())
      setActiveTab("current")
    } catch (error) {
      logger.error(error)
      
      // Extract error message if available
      const errorMessage = error instanceof Error ? error.message : 'Failed to schedule commissions. Please try again.'
      
      // Show error toast
      toast({
        title: dictionary?.dashboard?.messages?.releaseError ?? "Error",
        description: errorMessage,
        variant: "destructive"
      })
    }
  }, [
    selectedFutureIds,
    scheduleTransactions,
    refreshDashboardData,
    isAuthenticated,
    token,
    toast,
    dictionary,
    payoutType,
    currentPeriod,
  ])

  const handleDeferCommission = useCallback(async (deferToDate: Date) => {
    if (!isAuthenticated || !token || selectedCurrentIds.size === 0) return

    setIsDeferring(true)
    try {
      const selectedIds = Array.from(selectedCurrentIds)
      await deferTransactions(selectedIds, deferToDate)

      toast({
        title: dictionary?.dashboard?.messages?.deferSuccess ?? "Success",
        description: `${selectedIds.length} ${dictionary?.dashboard?.messages?.deferSuccessDescription ?? "transaction(s) deferred successfully."}`,
        variant: "success"
      })

      setIsDeferModalOpen(false)
      await refreshDashboardData()
      setSelectedCurrentIds(new Set())
      setActiveTab("future")
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to defer transactions. Please try again.'
      logger.error(error, {
        transactionIds: Array.from(selectedCurrentIds),
        targetDate: deferToDate.toISOString(),
        errorMessage,
      })

      toast({
        title: dictionary?.dashboard?.messages?.deferError ?? "Error",
        description: errorMessage,
        variant: "destructive"
      })
    } finally {
      setIsDeferring(false)
    }
  }, [
    selectedCurrentIds,
    deferTransactions,
    refreshDashboardData,
    isAuthenticated,
    token,
    toast,
    dictionary,
  ])

  const handleGenerateDraws = useCallback(async () => {
    if (!isAuthenticated || !token) return

    setActionDialog(null)
    setIsGeneratingDraws(true)
    try {
      // Call the API to generate recurring adjustments for the current period.
      const result = await generateRecurringAdjustments()
      
      // Show success toast
      toast({
        title: dictionary?.dashboard?.messages?.generateSuccess ?? "Success",
        description: `${result.transactionsCreated} ${dictionary?.dashboard?.messages?.generateSuccessDescription ?? "recurring adjustment transaction(s) generated successfully."}`,
        variant: "success"
      })

      // Refresh the dashboard data
      const { current } = await refreshDashboardData()
      
      // Select the newly created transactions in the current tab
      setSelectedCurrentIds(new Set(current.map(t => t.id)))
    } catch (error) {
      logger.error(error)
      
      // Extract error message if available
      const errorMessage = error instanceof Error ? error.message : (dictionary?.dashboard?.messages?.generateErrorDescription ?? "Failed to generate recurring adjustments. Please try again.")
      
      // Show error toast
      toast({
        title: dictionary?.dashboard?.messages?.generateError ?? "Error",
        description: errorMessage,
        variant: "destructive"
      })
    } finally {
      setIsGeneratingDraws(false)
    }
  }, [
    isAuthenticated,
    token,
    currentPeriod,
    generateRecurringAdjustments,
    refreshDashboardData,
    toast,
    dictionary
  ])

  const handleFinalizePeriod = useCallback(async () => {
    if (!currentPeriod) return

    setActionDialog(null)
    setIsFinalizing(true)
    try {
      await finalizePeriod(currentPeriod.timesheetEndDate)

      toast({
        title: dictionary?.dashboard?.messages?.finalizeSuccess ?? "Success",
        description: dictionary?.dashboard?.messages?.finalizeSuccessDescription ?? "Period finalized successfully. Moving to next period.",
        variant: "success"
      })

      const nextPeriod = await getCurrentPeriod()
      setCurrentPeriod(nextPeriod)

      await refreshDashboardData()

      setHasProceeded(false)
      setSelectedCurrentIds(new Set())
      setSelectedFutureIds(new Set())
      setOpeningBalances({})
    } catch (error) {
      logger.error(error)

      const errorMessage = error instanceof Error ? error.message : (dictionary?.dashboard?.messages?.finalizeErrorDescription ?? "Failed to finalize period.")

      toast({
        title: dictionary?.dashboard?.messages?.finalizeError ?? "Error",
        description: errorMessage,
        variant: "destructive"
      })
    } finally {
      setIsFinalizing(false)
    }
  }, [currentPeriod, finalizePeriod, getCurrentPeriod, refreshDashboardData, toast, dictionary])

  // Get selected transactions from current tab only (for cards and payout)
  const selectedCurrentTransactions = useMemo(() => {
    return currentTabTransactions.filter(t => selectedCurrentIds.has(t.id))
  }, [currentTabTransactions, selectedCurrentIds])

  // Card totals based on selected transactions from current tab only
  const grandTotal = useMemo(() => 
    selectedCurrentTransactions.reduce((acc, t) => acc + t.commissionAmount, 0),
    [selectedCurrentTransactions]
  )

  const permanentPlacementStatusBreakdown = useMemo(() => {
    const breakdown: Record<string, number> = {}
    const permanentPlacements = selectedCurrentTransactions.filter(t => t.transactionTypeCode === TransactionType.PermanentPlacement)
    permanentPlacements.forEach(t => {
      const status = t.commissionTransactionStatusCode || ''
      breakdown[status] = (breakdown[status] || 0) + 1
    })
    return Object.entries(breakdown)
      .map(([status, count]) => `${count} ${getCodeText(CodeValueGroups.CommissionTransactionStatus, status)}`)
      .join(', ')
  }, [getCodeText, selectedCurrentTransactions])

  const drawsAndAdjustmentsData = useMemo(() => {
    const drawsAndAdjustments = selectedCurrentTransactions.filter(t => t.isRecurringCommission === true)
    const total = drawsAndAdjustments.reduce((sum, t) => sum + t.commissionAmount, 0)
    return {
      count: drawsAndAdjustments.length,
      total
    }
  }, [selectedCurrentTransactions])

  // Show loading state while initializing
  if (!isAuthenticated || !token) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        {!isAuthenticated || !token ? dictionary?.app?.loading?.initializing ?? "Initializing..." : dictionary?.app?.loading?.transactions ?? "Loading transactions..."}
      </div>
    )
  }

  return (
    <div className="container mx-auto py-10 px-4 max-w-[1550px]">
      <h1 className="text-3xl font-bold mb-6">{dictionary.dashboard.title}</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              <DollarSignIcon className="h-4 w-4 inline-block mr-2 text-muted-foreground" />
              {dictionary.dashboard.cards.pendingCommissions.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(grandTotal)}</div>
            <p className="text-xs text-muted-foreground">
              {selectedCurrentTransactions.length} {dictionary.dashboard.cards.pendingCommissions.transactions}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              <FileTextIcon className="h-4 w-4 inline-block mr-2 text-muted-foreground" />
              {dictionary.dashboard.cards.permanentPlacements.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{selectedCurrentTransactions.filter(t => t.transactionTypeCode === TransactionType.PermanentPlacement).length}</div>
            <p className="text-xs text-muted-foreground">
              {permanentPlacementStatusBreakdown}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              <MinusCircleIcon className="h-4 w-4 inline-block mr-2 text-muted-foreground" />
              {dictionary.dashboard.cards.drawsAndAdjustments.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(drawsAndAdjustmentsData.total)}</div>
            <p className="text-xs text-muted-foreground">
              {drawsAndAdjustmentsData.count} {dictionary.dashboard.cards.drawsAndAdjustments.transactions}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              <CalendarIcon className="h-4 w-4 inline-block mr-2 text-muted-foreground" />
              {dictionary.dashboard.cards.lastPayout.title}
            </CardTitle>
            <Button 
              variant="ghost" 
              className="text-sm text-muted-foreground hover:text-foreground h-auto p-0"
              onClick={() => navigateTo('history')}
            >
              <span className={`flex items-center justify-center w-full`}>
                  {dictionary.dashboard.actions.viewHistory}<ChevronRightIcon className="ml-2 h-4 w-4" />
              </span>
            </Button>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{lastPayoutText}</div>
            {processedDateText && <p className="text-xs text-muted-foreground">{processedDateText}</p>}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-10">
        <Card>
          <CardHeader>
            <CardTitle>{dictionary.dashboard.commissionSummary.title}</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Payout Type Selection Panel — two column layout */}
            <div className="border rounded-lg p-4 mb-6 bg-gray-50">
              <div className="flex gap-6">
              {/* Left column: payout type, period info, actions */}
              <div className="flex-1">
              <label className="block text-sm font-medium mb-4">
                {dictionary?.prepare?.payoutDetails?.payoutType ?? "Payout Type"}
              </label>
              <RadioGroup value={payoutType} onValueChange={(value) => setPayoutType(value as PayoutType)}>
                <div className="space-y-3">
                  {/* Scheduled Payout Option */}
                  <div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="scheduled" id="scheduled-dashboard" />
                      <Label htmlFor="scheduled-dashboard" className="font-normal cursor-pointer">
                        {dictionary?.prepare?.payoutDetails?.scheduledPayout ?? "Scheduled Payout"}
                      </Label>
                    </div>
                    {/* Current Period Info + Period Actions - Indented under Scheduled Payout */}
                    {payoutType === 'scheduled' && currentPeriod && (
                      <div className="ml-6 mt-2 space-y-2">
                        <div className="text-sm flex items-center gap-4 flex-wrap">
                          <span>
                            <span className="text-muted-foreground">{(dictionary?.prepare?.payoutDetails?.currentPeriodEnding ?? "Current Period Ending:") + " "}</span>
                            <span className="font-medium">{formatDate(currentPeriod.timesheetEndDate, 'MMM d, yyyy', language)}</span>
                          </span>
                          <span>
                            <span className="text-muted-foreground">{(dictionary?.prepare?.payoutDetails?.scheduledPayoutDate ?? "Scheduled Payout:") + " "}</span>
                            <span className="font-medium">{formatDate(currentPeriod.plannedPaymentDate, 'MMM d, yyyy', language)}</span>
                          </span>
                          {isPeriodOverdue && (
                            <Badge variant="destructive" className="text-xs">{dictionary?.dashboard?.actions?.overdue ?? "Overdue"}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="bg-white hover:bg-gray-50"
                            disabled={isGeneratingDraws}
                            onClick={() => setActionDialog('generateDraws')}
                          >
                            {dictionary?.dashboard?.actions?.generateDraws ?? "Generate Draws and Recurring Adjustments"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="bg-white hover:bg-gray-50"
                            disabled={isFinalizing}
                            onClick={() => setActionDialog('finalize')}
                          >
                            {isFinalizing ? (dictionary?.dashboard?.actions?.finalizing ?? "Finalizing...") : (dictionary?.dashboard?.actions?.finalizePeriod ?? "Finalize Period")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Adhoc Payout Option */}
                  <div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="adhoc" id="adhoc-dashboard" />
                      <Label htmlFor="adhoc-dashboard" className="font-normal cursor-pointer">
                        {dictionary?.prepare?.payoutDetails?.adhocPayout ?? "Adhoc Payout"}
                      </Label>
                    </div>
                    {/* Employee Selection - Indented under Adhoc Payout */}
                    {payoutType === 'adhoc' && (
                      <div className="ml-6 mt-2">
                        <label className="block text-sm text-muted-foreground mb-1">
                          {dictionary?.prepare?.payoutDetails?.selectEmployee ?? "Select Employee"}
                        </label>
                        <Select 
                          value={selectedEmployeeId?.toString() ?? ''} 
                          onValueChange={(value) => setSelectedEmployeeId(value ? parseInt(value, 10) : null)}
                        >
                          <SelectTrigger className="w-[250px] bg-white">
                            <SelectValue placeholder={dictionary?.prepare?.payoutDetails?.selectEmployeePlaceholder ?? "Choose an employee..."} />
                          </SelectTrigger>
                          <SelectContent>
                            {commissionUsers.map((user) => (
                              <SelectItem key={user.profileId} value={user.profileId.toString()}>
                                {user.firstName} {user.lastName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              </RadioGroup>

              {/* Separator line */}
              <div className="h-px bg-border my-4" />

              <Button
                size="sm"
                disabled={!canProceed || isProceeding}
                onClick={handleProceedClick}
              >
                {dictionary?.dashboard?.actions?.loadTransactions ?? "Load Transactions"}
              </Button>
              </div> {/* end left column */}

              {/* Right column: summary + prepare button (only after proceeding) */}
              {hasProceeded && !isProceeding && (
              <div className="flex-1 border-l pl-6">
                <div className="space-y-2">
                  {payoutType === 'adhoc' ? (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{dictionary.dashboard.commissionSummary.standardCommissions}</span>
                        <span className="text-sm font-medium">{formatCurrency(payoutSummary.contractCommissions)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{dictionary.dashboard.commissionSummary.permanentCommissions}</span>
                        <span className="text-sm font-medium">{formatCurrency(payoutSummary.permPlacementCommissions)}</span>
                      </div>
                      <div className="h-px bg-border my-2" />
                      <div className="flex justify-between items-center font-medium">
                        <span>{dictionary.dashboard.commissionSummary.total}</span>
                        <span>{formatCurrency(payoutSummary.contractCommissions + payoutSummary.permPlacementCommissions)}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{dictionary?.dashboard?.commissionSummary?.openingBalance ?? "Opening Balance"}</span>
                        <span className="text-sm font-medium">{formatCurrency(payoutSummary.openingBalance)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{dictionary.dashboard.commissionSummary.standardCommissions}</span>
                        <span className="text-sm font-medium">{formatCurrency(payoutSummary.contractCommissions)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{dictionary.dashboard.commissionSummary.permanentCommissions}</span>
                        <span className="text-sm font-medium">{formatCurrency(payoutSummary.permPlacementCommissions)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{dictionary?.dashboard?.commissionSummary?.adjustments ?? "Adjustments"}</span>
                        <span className="text-sm font-medium">{formatCurrency(payoutSummary.adjustments)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{dictionary?.dashboard?.commissionSummary?.draws ?? "Draws"}</span>
                        <span className="text-sm font-medium">{formatCurrency(-payoutSummary.draws)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{dictionary?.dashboard?.commissionSummary?.closingBalance ?? "Closing Balance (carried forward)"}</span>
                        <span className="text-sm font-medium">{formatCurrency(payoutSummary.closingBalance)}</span>
                      </div>
                      <div className="h-px bg-border my-2" />
                      <div className="flex justify-between items-center font-medium">
                        <span>{dictionary?.dashboard?.commissionSummary?.totalPayable ?? "Total Payable"}</span>
                        <span>{formatCurrency(payoutSummary.totalPayable)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm text-muted-foreground">
                        <span>{dictionary?.dashboard?.commissionSummary?.includesDrawsPayable ?? "Includes Draws Payable"}</span>
                        <span>{formatCurrency(payoutSummary.includesDrawsPayable)}</span>
                      </div>
                    </>
                  )}
                </div>
                <div className="mt-4">
                  <Button
                    className="w-full"
                    disabled={!payoutSummary.hasSelectedTransactions}
                    onClick={handlePrepareClick}
                  >
                    {dictionary.dashboard.actions.preparePayout} <ChevronRightIcon className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
              )}

              </div> {/* end flex row */}
            </div>
          </CardContent>
        </Card>

        {/* Action confirmation dialogs — outside hasProceeded so they work from the dropdown */}
        {actionDialog === 'generateDraws' && (
        <AlertDialog open onOpenChange={(open) => { if (!open) setActionDialog(null) }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {dictionary.dashboard.actions.generateDrawsConfirmTitle}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {dictionary.dashboard.actions.generateDrawsConfirmDescription.replace(
                  '{period}',
                  currentPeriod
                    ? formatDate(currentPeriod.timesheetEndDate)
                    : ''
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {dictionary.dashboard.actions.generateDrawsConfirmCancel}
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleGenerateDraws}>
                {dictionary.dashboard.actions.generateDrawsConfirmAction}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        )}

        {actionDialog === 'finalize' && (
        <AlertDialog open onOpenChange={(open) => { if (!open) setActionDialog(null) }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{dictionary?.dashboard?.actions?.finalizePeriodConfirmTitle ?? "Finalize Period"}</AlertDialogTitle>
              <AlertDialogDescription>
                {dictionary?.dashboard?.actions?.finalizePeriodConfirmDescription ?? "Are you sure you want to finalize this period? This action cannot be undone. Any late transactions will be moved to the next period."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{dictionary?.dashboard?.actions?.finalizePeriodConfirmCancel ?? "Cancel"}</AlertDialogCancel>
              <AlertDialogAction onClick={handleFinalizePeriod}>
                {dictionary?.dashboard?.actions?.finalizePeriodConfirmAction ?? "Finalize"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        )}
      </div>

      {/* Loading indicator while fetching data */}
      {isProceeding && (
        <Card>
          <CardContent>
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              {dictionary?.app?.loading?.transactions ?? "Loading..."}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Only show transaction grid after user proceeds */}
      {hasProceeded && !isProceeding && (
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>{dictionary.dashboard.transactions.title}</CardTitle>
              <CardDescription>{dictionary.dashboard.transactions.description}</CardDescription>
            </div>
            {Object.keys(filters).length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearFilters}
                className="self-end bg-[#ff0000] text-white hover:bg-[#ff0000] hover:text-white"
              >
                {dictionary.dashboard.actions.resetFilter}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs defaultValue="current" value={activeTab} onValueChange={setActiveTab}>
            <div className="px-6 pt-2">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="current">
                  {dictionary.dashboard.transactions.tabs.current} ({processedCurrentTransactions.length})
                </TabsTrigger>
                <TabsTrigger value="future">
                  {dictionary.dashboard.transactions.tabs.future} ({processedFutureTransactions.length})
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="current" className="p-0">
              <div className="p-4 border-t flex justify-between items-center">
                <div>
                  <span className="text-sm text-muted-foreground">
                    {selectedCurrentIds.size} of {currentTabTransactions.length} selected
                  </span>
                </div>
                <Button
                  disabled={selectedCurrentIds.size === 0}
                  onClick={() => setIsDeferModalOpen(true)}
                  size="sm"
                  aria-label={dictionary?.dashboard?.actions?.deferToFuture ?? "Defer to Future"}
                >
                  <ClockIcon className="h-4 w-4 mr-2" aria-hidden="true" />
                  {dictionary?.dashboard?.actions?.deferToFuture ?? "Defer to Future"}
                </Button>
              </div>
              <div className="relative px-4">
                <div className="border border-[#dddddd] relative">
                  <div style={{ overflowX: 'auto' }}>
                    <div style={{ 
                      minWidth: 'fit-content',
                      position: 'relative'
                    }}>
                      <Table className="w-full table-fixed">
                        <colgroup>
                          {showCheckbox && <col className="w-[50px]" />}
                          {columnsConfig.map((col) => (
                            <col key={String(col.key)} className={col.width} />
                          ))}
                        </colgroup>
                        <TableHeader className="sticky top-0 bg-white z-20">
                          <TableHeaderRow<CommissionTransaction>
                            columns={columnsConfig}
                            onColumnClick={handleColumnClick}
                            currentSortColumn={sortConfig.column}
                            currentSortDirection={sortConfig.direction}
                            showCheckbox={true}
                            isAllChecked={checkboxStates.currentAllChecked}
                            onCheckAll={handleCurrentSelectAll}
                          />
                        </TableHeader>
                        <TableBody>
                          <FilterRow<CommissionTransaction>
                            columns={columnsConfig}
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            dictionary={dictionary}
                            data={currentTabTransactions}
                            showCheckbox={true}
                          />
                        </TableBody>
                      </Table>
                      
                      <div style={{ 
                        maxHeight: '600px',
                        overflowY: 'scroll',
                        marginRight: '-17px'
                      }}>
                        <Table className="w-full table-fixed">
                          <colgroup>
                            {showCheckbox && <col className="w-[50px]" />}
                            {columnsConfig.map((col) => (
                              <col key={String(col.key)} className={col.width} />
                            ))}
                          </colgroup>
                          <TableBody>
                            {currentPageTransactions.map((item) => (
                              <TableRowContent<CommissionTransaction>
                                key={item.id}
                                item={item}
                                isSelected={selectedCurrentIds.has(item.id)}
                                onCheckboxChange={handleCurrentCheckboxChange}
                                language={language}
                                columnsConfig={columnsConfig}
                                showCheckbox={true}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="my-6 flex justify-end">
                  {currentPagination}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="future" className="p-0">
              <div className="p-4 border-t flex justify-between items-center">
                <div>
                  <span className="text-sm text-muted-foreground">
                    {selectedFutureIds.size} of {futureTabTransactions.length} selected
                  </span>
                </div>
                <Button
                  disabled={selectedFutureIds.size === 0}
                  onClick={handleReleaseCommission}
                  size="sm"
                >
                  <CheckIcon className="h-4 w-4 mr-2" />
                  {dictionary?.dashboard?.actions?.releaseCommission ?? "Release Commission"}
                </Button>
              </div>
              <div className="relative px-4">
                <div className="border border-[#dddddd] relative">
                  <div style={{ overflowX: 'auto' }}>
                    <div style={{ 
                      minWidth: 'fit-content',
                      position: 'relative'
                    }}>
                      <Table className="w-full table-fixed">
                        <colgroup>
                          {showCheckbox && <col className="w-[50px]" />}
                          {columnsConfig.map((col) => (
                            <col key={String(col.key)} className={col.width} />
                          ))}
                        </colgroup>
                        <TableHeader className="sticky top-0 bg-white z-20">
                          <TableHeaderRow<CommissionTransaction>
                            columns={columnsConfig}
                            onColumnClick={handleColumnClick}
                            currentSortColumn={sortConfig.column}
                            currentSortDirection={sortConfig.direction}
                            showCheckbox={true}
                            isAllChecked={checkboxStates.futureAllChecked}
                            onCheckAll={handleFutureSelectAll}
                          />
                        </TableHeader>
                        <TableBody>
                          <FilterRow<CommissionTransaction>
                            columns={columnsConfig}
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            dictionary={dictionary}
                            data={futureTabTransactions}
                            showCheckbox={true}
                          />
                        </TableBody>
                      </Table>
                      
                      <div style={{ 
                        maxHeight: '600px',
                        overflowY: 'scroll',
                        marginRight: '-17px'
                      }}>
                        <Table className="w-full table-fixed">
                          <colgroup>
                            {showCheckbox && <col className="w-[50px]" />}
                            {columnsConfig.map((col) => (
                              <col key={String(col.key)} className={col.width} />
                            ))}
                          </colgroup>
                          <TableBody>
                            {futurePageTransactions.map((item) => (
                              <TableRowContent<CommissionTransaction>
                                key={item.id}
                                item={item}
                                isSelected={selectedFutureIds.has(item.id)}
                                onCheckboxChange={handleFutureCheckboxChange}
                                language={language}
                                columnsConfig={columnsConfig}
                                showCheckbox={true}
                              />
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="my-6 flex justify-end">
                  {futurePagination}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      )}
      <DeferCommissionModal
        open={isDeferModalOpen}
        onOpenChange={setIsDeferModalOpen}
        onConfirm={handleDeferCommission}
        isLoading={isDeferring}
        dictionary={dictionary}
      />
    </div>
  )
}